import { SEO } from "@/components/seo/SEO";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Mail, MessageSquare, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const Contact = () => {
  return (
    <>
      <SEO 
        title="Contact Us - QuickMedicalReview"
        description="Get in touch with QuickMedicalReview support. We're here to help with any questions about your medical study journey."
        path="/contact"
      />
      
      <div className="container py-12 max-w-4xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Contact Us</h1>
          <p className="text-muted-foreground text-lg">
            Have questions? We're here to help you succeed in your medical studies.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Mail className="h-6 w-6 text-primary" />
                </div>
                <CardTitle>Email Support</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                Send us an email and we'll respond within 24 hours.
              </p>
              <Button asChild className="w-full">
                <a href="mailto:support@quickmedicalreview.com">
                  support@quickmedicalreview.com
                </a>
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <HelpCircle className="h-6 w-6 text-primary" />
                </div>
                <CardTitle>General Inquiries</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                Questions about features, subscriptions, or partnerships?
              </p>
              <Button asChild variant="outline" className="w-full">
                <a href="mailto:info@quickmedicalreview.com">
                  info@quickmedicalreview.com
                </a>
              </Button>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Frequently Asked Questions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h3 className="font-semibold mb-2">How do I cancel my subscription?</h3>
              <p className="text-muted-foreground">
                You can cancel your subscription anytime through your account dashboard. Your access will continue until the end of your billing period.
              </p>
            </div>

            <div>
              <h3 className="font-semibold mb-2">What happens after my free trial ends?</h3>
              <p className="text-muted-foreground">
                Your free trial automatically converts to a paid subscription unless you cancel before the trial period ends. You'll be notified before any charges.
              </p>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Can I change my subscription plan?</h3>
              <p className="text-muted-foreground">
                Yes! You can upgrade or downgrade your plan at any time. Changes will be reflected in your next billing cycle.
              </p>
            </div>

            <div>
              <h3 className="font-semibold mb-2">How can I report an error in a question?</h3>
              <p className="text-muted-foreground">
                Please email us at support@quickmedicalreview.com with the question ID and details about the error. We review all feedback to maintain content quality.
              </p>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Do you offer student discounts?</h3>
              <p className="text-muted-foreground">
                Contact us at info@quickmedicalreview.com to inquire about student discounts and institutional licenses.
              </p>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Is my data secure?</h3>
              <p className="text-muted-foreground">
                Yes! We use industry-standard encryption and security measures to protect your data. Read our{" "}
                <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a> for more details.
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="mt-12 p-6 bg-muted rounded-lg text-center">
          <MessageSquare className="h-12 w-12 text-primary mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Need Immediate Help?</h2>
          <p className="text-muted-foreground mb-4">
            For urgent technical issues or account problems, email us directly at{" "}
            <a href="mailto:support@quickmedicalreview.com" className="text-primary hover:underline font-medium">
              support@quickmedicalreview.com
            </a>
          </p>
          <p className="text-sm text-muted-foreground">
            We typically respond within 24 hours during business days.
          </p>
        </div>
      </div>
    </>
  );
};

export default Contact;
